import { Boxes, ClipboardList, KeyRound, Server, type LucideIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

import { useAppsQuery, useAuditQuery, usePublicKeyQuery, useStationsQuery } from "@/api/query-hooks";
import { Badge } from "@/components/ui/badge";
import { LoadingState } from "@/components/layout/route-states";

export function HomePage() {
  const { t } = useTranslation("common");
  const apps = useAppsQuery();
  const stationParams = new URLSearchParams({ limit: "10" });
  const stations = useStationsQuery(stationParams);
  const auditParams = new URLSearchParams({ limit: "10" });
  const audit = useAuditQuery(auditParams);
  const publicKey = usePublicKeyQuery();

  if (apps.isLoading || stations.isLoading || audit.isLoading || publicKey.isLoading) {
    return <LoadingState />;
  }

  return (
    <div className="grid gap-5">
      <div>
        <h2 className="text-lg font-semibold">{t("nav.overview")}</h2>
        <p className="mt-1 text-sm text-muted-foreground">Live platform API summary for the current dev stand.</p>
      </div>
      <div className="grid gap-3 md:grid-cols-4">
        <Metric icon={Boxes} label={t("nav.apps")} value={apps.data?.items.length ?? 0} />
        <Metric icon={Server} label={t("nav.stations")} value={stations.data?.items.length ?? 0} />
        <Metric icon={ClipboardList} label={t("nav.audit")} value={audit.data?.items.length ?? 0} />
        <Metric icon={KeyRound} label="Cert key" value={publicKey.data?.version ?? "—"} />
      </div>
      <section className="rounded-md border bg-panel p-4">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="font-medium">Status</h3>
          <Badge variant="success">online</Badge>
        </div>
        <div className="grid gap-2 text-sm text-muted-foreground">
          <div>API: /admin/v1</div>
          <div>Public key: {publicKey.data?.kind ?? "—"}</div>
        </div>
      </section>
    </div>
  );
}

function Metric({ icon: Icon, label, value }: { icon: LucideIcon; label: string; value: number | string }) {
  return (
    <section className="rounded-md border bg-panel p-4">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="truncate text-sm text-muted-foreground">{label}</div>
          <div className="mt-1 text-2xl font-semibold">{value}</div>
        </div>
        <Icon className="h-5 w-5 text-primary" />
      </div>
    </section>
  );
}
